/**
 * Evidence photos: camera capture (native camera plugin, or the browser's
 * camera via <input capture>), gallery pick, thumbnails, delete. Compression
 * and storage happen in the parent's onAdd (see SectionForm).
 */
import { useEffect, useRef, useState } from 'react';
import { Badge, Button, Dialog, Icon, Spinner } from '../../components';
import { isNative } from '../../lib/device';
import { useI18n } from '../../lib/i18n';
import { analyzePhotoQuality, type PhotoQualityStatus } from '../../lib/media/photo-quality';
import { takeNativePhoto } from '../../lib/native';
import { PhotoBlurModal } from './PhotoBlurModal';

export interface PhotoInputProps {
  ids: string[];
  onAdd: (files: Blob[]) => Promise<void>;
  onRemove: (id: string) => Promise<void>;
  getUrl: (id: string) => Promise<string | null>;
  /** Type and name of a stored file: documents show as a file tile, not a broken image. */
  getMeta?: (id: string) => Promise<{ contentType: string; fileName: string } | null>;
  max?: number;
  disabled?: boolean;
  required?: boolean;
  accept?: string;
}

function Thumb({
  id,
  getUrl,
  getMeta,
  onRemove,
  onBlur,
  disabled,
  label,
  blurLabel,
}: {
  id: string;
  getUrl: PhotoInputProps['getUrl'];
  getMeta?: PhotoInputProps['getMeta'];
  onRemove: () => void;
  onBlur: (url: string) => void;
  disabled?: boolean;
  label: string;
  blurLabel: string;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [meta, setMeta] = useState<{ contentType: string; fileName: string } | null>(null);
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    let revoked: string | null = null;
    let alive = true;
    void getUrl(id).then((u) => {
      revoked = u;
      if (alive) setUrl(u);
    });
    void getMeta?.(id).then((m) => alive && setMeta(m), () => {});
    return () => {
      alive = false;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [id, getUrl, getMeta]);
  const isDocument = broken || (!!meta && !meta.contentType.startsWith('image/'));
  return (
    <div className="relative size-24 overflow-hidden rounded-xl border border-border bg-surface-2" title={meta?.fileName}>
      {isDocument ? (
        <span className="flex size-full flex-col items-center justify-center gap-1 p-1 text-center">
          <Icon name="paperclip" className="text-brand" />
          <span className="line-clamp-2 break-all text-[10px] leading-tight text-muted">{meta?.fileName ?? ''}</span>
        </span>
      ) : url ? (
        <img src={url} alt="" className="size-full object-cover" onError={() => setBroken(true)} />
      ) : (
        <Icon name="image" className="m-auto mt-8 text-muted" />
      )}
      {!disabled && (
        <>
          {!isDocument && url && (
            <button
              type="button"
              onClick={() => onBlur(url)}
              aria-label={blurLabel}
              title={blurLabel}
              className="absolute start-1 top-1 flex size-8 items-center justify-center rounded-full bg-black/60 text-white"
            >
              <Icon name="shield" size={14} />
            </button>
          )}
          <button type="button" onClick={onRemove} aria-label={label} className="absolute end-1 top-1 flex size-8 items-center justify-center rounded-full bg-black/60 text-white">
            <Icon name="x" size={16} />
          </button>
        </>
      )}
    </div>
  );
}

export function PhotoInput({ ids, onAdd, onRemove, getUrl, getMeta, max = 10, disabled, required, accept = 'image/*' }: PhotoInputProps) {
  const { t } = useI18n();
  const camera = useRef<HTMLInputElement>(null);
  const gallery = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [blurTarget, setBlurTarget] = useState<{ id: string; url: string } | null>(null);
  const [coachStatus, setCoachStatus] = useState<PhotoQualityStatus | null>(null);
  const full = ids.length >= max;
  // Photo pickers accept images only; a file picker may also accept images among documents.
  const isImage = accept.split(',').every((a) => a.trim().startsWith('image/'));

  const add = async (files: Blob[]) => {
    if (!files.length) return;
    setBusy(true);
    try {
      const batch = files.slice(0, max - ids.length);
      const firstImg = batch.find((f) => f.type.startsWith('image/'));
      if (firstImg) {
        const q = await analyzePhotoQuality(firstImg);
        setCoachStatus(q.status);
      }
      await onAdd(batch);
    } finally {
      setBusy(false);
    }
  };

  const takePhoto = async () => {
    if (isNative() && isImage) {
      const blob = await takeNativePhoto().catch(() => null);
      if (blob) await add([blob]);
      return;
    }
    camera.current?.click();
  };

  return (
    <div className="flex flex-col gap-2">
      {ids.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {ids.map((id) => (
            <Thumb
              key={id}
              id={id}
              getUrl={getUrl}
              getMeta={getMeta}
              disabled={disabled}
              label={t('ui.capture.removePhoto')}
              blurLabel={t('ui.capture.blurPhoto')}
              onRemove={() => setConfirmId(id)}
              onBlur={(url) => setBlurTarget({ id, url })}
            />
          ))}
        </div>
      )}
      {!disabled && !full && (
        <div className="flex flex-wrap gap-2">
          <Button variant={required ? 'primary' : 'secondary'} icon={isImage ? 'camera' : 'paperclip'} onClick={takePhoto} loading={busy}>
            {isImage ? t('ui.capture.takePhoto') : t('ui.capture.attachFile')}
          </Button>
          {isImage && (
            <Button variant="ghost" icon="image" onClick={() => gallery.current?.click()} disabled={busy}>
              {t('ui.capture.fromGallery')}
            </Button>
          )}
          {busy && <Spinner />}
        </div>
      )}
      {coachStatus && ids.length > 0 && (
        <div className="flex items-center gap-2 text-xs">
          <Badge tone={coachStatus === 'ok' ? 'success' : 'warning'} icon={coachStatus === 'ok' ? 'checkCircle' : 'alert'}>
            {coachStatus === 'ok'
              ? t('ui.excellence.photoCoach.ok')
              : coachStatus === 'dark'
                ? t('ui.excellence.photoCoach.dark')
                : coachStatus === 'glare'
                  ? t('ui.excellence.photoCoach.glare')
                  : t('ui.excellence.photoCoach.blurry')}
          </Badge>
        </div>
      )}
      <input ref={camera} type="file" accept={accept} capture={isImage ? 'environment' : undefined} hidden onChange={(e) => void add(Array.from(e.target.files ?? [])).finally(() => (e.target.value = ''))} />
      <input ref={gallery} type="file" accept={accept} multiple hidden onChange={(e) => void add(Array.from(e.target.files ?? [])).finally(() => (e.target.value = ''))} />
      <Dialog
        open={!!confirmId}
        onClose={() => setConfirmId(null)}
        title={t('ui.capture.removePhotoConfirm')}
        tone="danger"
        actions={
          <>
            <Button variant="secondary" onClick={() => setConfirmId(null)}>
              {t('ui.common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={async () => {
                const id = confirmId!;
                setConfirmId(null);
                await onRemove(id);
              }}
            >
              {t('ui.common.delete')}
            </Button>
          </>
        }
      />
      {blurTarget && (
        <PhotoBlurModal
          open
          imageUrl={blurTarget.url}
          onClose={() => setBlurTarget(null)}
          onApply={async (blurredBlob) => {
            const oldId = blurTarget.id;
            await onAdd([blurredBlob]);
            await onRemove(oldId);
            setBlurTarget(null);
          }}
        />
      )}
    </div>
  );
}
